import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import NavArrowButton from '../../atoms/NavArrowButton';
import './index.scss';

const AUTO_SCROLL_DIRECTIONS = new Set(['left', 'right']);
const DEFAULT_AUTO_SCROLL_SPEED = 18;
const AUTO_SCROLL_RESUME_DELAY_MS = 1200;

/**
 * Horizontal-scrolling rail of cards laid out over a fixed number of rows —
 * the same approach the photo gallery (Images) uses: a hidden-scrollbar scroller
 * with chevron nav buttons that appear only when the content overflows.
 *
 * The grid track flows items column-by-column across `rows` rows. Column width,
 * gap and the minimum item size are CSS custom properties so each consumer can
 * tune them (see `--ed-rail-*` in index.scss).
 */
export default function EditorialCardRail({
  children,
  rows = 1,
  className = '',
  trackClassName = '',
  previousLabel = 'Scroll left',
  nextLabel = 'Scroll right',
  autoScrollDirection,
  autoScrollSpeed = DEFAULT_AUTO_SCROLL_SPEED,
}) {
  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  const autoScrollResumeTimeoutRef = useRef(null);
  const isPointerHoveringRef = useRef(false);
  const isPointerInteractingRef = useRef(false);
  const childItems = React.Children.toArray(children);
  const normalizedRows = Math.max(1, rows);
  const normalizedAutoScrollDirection = AUTO_SCROLL_DIRECTIONS.has(autoScrollDirection)
    ? autoScrollDirection
    : null;
  const [scrollState, setScrollState] = useState({
    canScrollPrevious: false,
    canScrollNext: false,
    hasContentOverflow: false,
  });
  const [autoScrollSegmentWidth, setAutoScrollSegmentWidth] = useState(0);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const isAutoScrollActive = Boolean(
    normalizedAutoScrollDirection
    && scrollState.hasContentOverflow
    && !prefersReducedMotion
    && childItems.length > 1
    && normalizedRows === 1
  );

  const getContentMetrics = useCallback((scroller) => {
    if (!scroller) {
      return { contentWidth: 0, segmentWidth: 0 };
    }

    const track = trackRef.current;
    if (track && childItems.length > 0) {
      const firstItem = track.children[0];
      const lastOriginalItem = track.children[childItems.length - 1];
      const firstCloneItem = isAutoScrollActive ? track.children[childItems.length] : null;
      const startOffset = firstItem?.offsetLeft ?? 0;
      const measuredContentWidth = lastOriginalItem
        ? (lastOriginalItem.offsetLeft + lastOriginalItem.offsetWidth) - startOffset
        : 0;
      const measuredSegmentWidth = firstCloneItem
        ? firstCloneItem.offsetLeft - startOffset
        : 0;
      const fallbackContentWidth = isAutoScrollActive
        ? Math.max(0, scroller.scrollWidth / 2)
        : (track.scrollWidth || scroller.scrollWidth);
      const contentWidth = measuredContentWidth || fallbackContentWidth;
      const segmentWidth = measuredSegmentWidth || fallbackContentWidth || contentWidth;
      return { contentWidth, segmentWidth };
    }

    const contentWidth = track?.scrollWidth || scroller.scrollWidth;
    return { contentWidth, segmentWidth: contentWidth };
  }, [childItems.length, isAutoScrollActive]);

  const updateScrollState = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      setScrollState({
        canScrollPrevious: false,
        canScrollNext: false,
        hasContentOverflow: false,
      });
      return;
    }
    const { contentWidth } = getContentMetrics(scroller);
    const maxScrollLeft = Math.max(0, contentWidth - scroller.clientWidth);
    const nextState = {
      canScrollPrevious: scroller.scrollLeft > 1,
      canScrollNext: scroller.scrollLeft < maxScrollLeft - 1,
      hasContentOverflow: contentWidth > scroller.clientWidth + 1,
    };
    setScrollState((current) => (
      current.canScrollPrevious === nextState.canScrollPrevious
      && current.canScrollNext === nextState.canScrollNext
      && current.hasContentOverflow === nextState.hasContentOverflow
        ? current
        : nextState
    ));
  }, [getContentMetrics]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(motionQuery.matches);
    const handleMotionPreferenceChange = (event) => {
      setPrefersReducedMotion(Boolean(event.matches));
    };
    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', handleMotionPreferenceChange);
      return () => motionQuery.removeEventListener('change', handleMotionPreferenceChange);
    }
    if (typeof motionQuery.addListener === 'function') {
      motionQuery.addListener(handleMotionPreferenceChange);
      return () => motionQuery.removeListener(handleMotionPreferenceChange);
    }
    return undefined;
  }, []);

  const clearAutoScrollResumeTimeout = useCallback(() => {
    if (autoScrollResumeTimeoutRef.current != null && typeof window !== 'undefined') {
      window.clearTimeout(autoScrollResumeTimeoutRef.current);
      autoScrollResumeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    clearAutoScrollResumeTimeout();
  }, [clearAutoScrollResumeTimeout]);

  const resumeAutoScroll = useCallback(() => {
    clearAutoScrollResumeTimeout();
    if (isPointerHoveringRef.current || isPointerInteractingRef.current) {
      return;
    }
    setIsAutoScrollPaused(false);
  }, [clearAutoScrollResumeTimeout]);

  const pauseAutoScrollForUserInput = useCallback((resumeDelayMs = AUTO_SCROLL_RESUME_DELAY_MS) => {
    if (!isAutoScrollActive || typeof window === 'undefined') {
      return;
    }
    setIsAutoScrollPaused(true);

    clearAutoScrollResumeTimeout();
    if (resumeDelayMs > 0) {
      autoScrollResumeTimeoutRef.current = window.setTimeout(() => {
        autoScrollResumeTimeoutRef.current = null;
        resumeAutoScroll();
      }, resumeDelayMs);
    }
  }, [
    clearAutoScrollResumeTimeout,
    isAutoScrollActive,
    resumeAutoScroll,
  ]);

  useEffect(() => {
    updateScrollState();
    const scroller = scrollerRef.current;
    if (!scroller) {
      return undefined;
    }
    const handleScrollerScroll = () => {
      updateScrollState();
    };
    scroller.addEventListener('scroll', handleScrollerScroll, { passive: true });
    const handleResize = () => updateScrollState();
    window.addEventListener('resize', handleResize);
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(scroller);
      if (trackRef.current) {
        resizeObserver.observe(trackRef.current);
      }
    }
    return () => {
      scroller.removeEventListener('scroll', handleScrollerScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [updateScrollState, children]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!isAutoScrollActive || !scroller) {
      setAutoScrollSegmentWidth(0);
      return undefined;
    }
    const { segmentWidth } = getContentMetrics(scroller);
    setAutoScrollSegmentWidth((current) => (
      Math.abs(current - segmentWidth) < 1 ? current : segmentWidth
    ));
    return undefined;
  }, [
    getContentMetrics,
    isAutoScrollActive,
  ]);

  const handleScroll = useCallback((direction) => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    pauseAutoScrollForUserInput();
    if (isAutoScrollActive) {
      const { segmentWidth } = getContentMetrics(scroller);
      if (direction === 'previous' && scroller.scrollLeft <= 1) {
        scroller.scrollLeft += segmentWidth;
      }
      if (direction === 'next' && scroller.scrollLeft >= segmentWidth - 1) {
        scroller.scrollLeft -= segmentWidth;
      }
    }
    const scrollAmount = Math.max(280, scroller.clientWidth * 0.85);
    scroller.scrollBy({
      left: direction === 'previous' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
    window.setTimeout(() => {
      updateScrollState();
    }, 240);
  }, [
    getContentMetrics,
    isAutoScrollActive,
    pauseAutoScrollForUserInput,
    updateScrollState,
  ]);

  const handleWheelCapture = useCallback((event) => {
    const isHorizontalIntent = Math.abs(event.deltaX) > 0 || event.shiftKey;
    if (isHorizontalIntent) {
      pauseAutoScrollForUserInput();
    }
  }, [pauseAutoScrollForUserInput]);

  const handlePointerDownCapture = useCallback(() => {
    isPointerInteractingRef.current = true;
    pauseAutoScrollForUserInput(0);
  }, [pauseAutoScrollForUserInput]);

  const handlePointerReleaseCapture = useCallback(() => {
    isPointerInteractingRef.current = false;
    if (isPointerHoveringRef.current) {
      pauseAutoScrollForUserInput(0);
      return;
    }
    pauseAutoScrollForUserInput();
  }, [pauseAutoScrollForUserInput]);

  const handlePointerEnter = useCallback(() => {
    isPointerHoveringRef.current = true;
    pauseAutoScrollForUserInput(0);
  }, [pauseAutoScrollForUserInput]);

  const handlePointerLeave = useCallback(() => {
    isPointerHoveringRef.current = false;
    if (isPointerInteractingRef.current) {
      return;
    }
    resumeAutoScroll();
  }, [resumeAutoScroll]);

  const hasOverflow = scrollState.hasContentOverflow;
  const rootClassName = [
    'ed-card-rail',
    isAutoScrollActive ? 'ed-card-rail--auto-active' : '',
    isAutoScrollActive && isAutoScrollPaused ? 'ed-card-rail--auto-paused' : '',
    className,
  ].filter(Boolean).join(' ');
  const canScrollPrevious = isAutoScrollActive ? true : scrollState.canScrollPrevious;
  const canScrollNext = isAutoScrollActive ? true : scrollState.canScrollNext;
  const normalizedAutoScrollSpeed = Number.isFinite(autoScrollSpeed) && autoScrollSpeed > 0
    ? autoScrollSpeed
    : DEFAULT_AUTO_SCROLL_SPEED;
  const autoScrollDurationSeconds = autoScrollSegmentWidth > 0
    ? autoScrollSegmentWidth / normalizedAutoScrollSpeed
    : 0;

  return (
    <div
      className={rootClassName}
      data-auto-scroll-direction={isAutoScrollActive ? normalizedAutoScrollDirection : undefined}
      onPointerDownCapture={handlePointerDownCapture}
      onPointerUpCapture={handlePointerReleaseCapture}
      onPointerCancelCapture={handlePointerReleaseCapture}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onWheelCapture={handleWheelCapture}
    >
      {hasOverflow ? (
        <div className="ed-card-rail__controls">
          <NavArrowButton
            direction="previous"
            ariaLabel={previousLabel}
            className="ed-card-rail__nav"
            onClick={() => handleScroll('previous')}
            disabled={!canScrollPrevious}
          />
          <NavArrowButton
            direction="next"
            ariaLabel={nextLabel}
            className="ed-card-rail__nav"
            onClick={() => handleScroll('next')}
            disabled={!canScrollNext}
          />
        </div>
      ) : null}
      <div className="ed-card-rail__scroll" ref={scrollerRef}>
        <div
          ref={trackRef}
          className={`ed-card-rail__track ${trackClassName}`.trim()}
          style={{
            '--ed-rail-rows': String(normalizedRows),
            ...(isAutoScrollActive && autoScrollSegmentWidth > 0 ? {
              '--ed-rail-auto-duration': `${autoScrollDurationSeconds}s`,
              '--ed-rail-loop-distance': `${autoScrollSegmentWidth}px`,
            } : {}),
          }}
        >
          {childItems}
          {isAutoScrollActive ? childItems.map((child, index) => (
            <div
              className="ed-card-rail__loop-clone"
              key={`auto-loop-${child.key || index}`}
            >
              {child}
            </div>
          )) : null}
        </div>
      </div>
    </div>
  );
}

EditorialCardRail.propTypes = {
  children: PropTypes.node,
  rows: PropTypes.number,
  className: PropTypes.string,
  trackClassName: PropTypes.string,
  previousLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  autoScrollDirection: PropTypes.oneOf(['left', 'right']),
  autoScrollSpeed: PropTypes.number,
};
